import { useMemo } from 'react';
import classNames from 'classnames';
import { Popover } from 'components-antd';

import { useOutsideClick } from 'hooks';

import styles from './styles.module.scss';

const Suggestion = ({ label, detail, onClick }) => {
  return (
    <div onMouseDown={(e) => e.preventDefault()} className={styles.suggestion} onClick={onClick}>
      <span className={styles.offset}>{label}</span>
      <span className={styles.detail}>({detail})</span>
    </div>
  );
};

export const DateControlSuggestions = ({
  dateControlRef,
  value,
  open,
  setOpen,
  onSuggestionClick,
  inputField,
  className = '',
  isTemplate = false,
  isProject,
  showClosingDate = true,
  isPopover = false,
}) => {
  if (!isPopover) {
    useOutsideClick([dateControlRef], () => {
      setOpen(false);
    });
  }

  const suggestionsList = useMemo(() => {
    const baseSuggestions = [
      ...(isTemplate
        ? [{ label: 'A', detail: 'Assigned Date' }]
        : [{ label: 'T', detail: "Today's Date" }]),
      ...(isProject
        ? [
            { label: 'KO', detail: 'Kickoff Date' },
            { label: 'DL', detail: 'Deadline Date' },
          ]
        : [{ label: 'CD', detail: 'Contract Date' }]),
      ...(!isProject && (showClosingDate || isTemplate)
        ? [{ label: 'CL', detail: 'Closing Date' }]
        : []),
      { label: 'LE', detail: 'Listing Expiration Date' },
    ];

    if (!value) return baseSuggestions; // Show all options when value is empty

    // Regex to check if value is in the format of "LABEL+NUMBER" or "LABEL-NUMBER"
    const match = value.match(/^([A-Z]+)([+-])(\d+)$/);
    if (match) {
      const [, baseLabel, operator, number] = match;
      const selectedOption = baseSuggestions.find((option) => option.label === baseLabel);

      if (selectedOption) {
        return [
          {
            label: value, // Keep the full input (e.g., "T+5")
            detail: `${number} Days ${operator === '+' ? 'after' : 'before'} ${
              selectedOption.detail
            }`,
          },
        ];
      }
    }

    // Find exact match from base suggestions
    const selectedOption = baseSuggestions.find((option) => option.label === value);
    if (selectedOption) {
      return [
        { label: `${value}+`, detail: `Days after ${selectedOption.detail}` },
        { label: `${value}-`, detail: `Days before ${selectedOption.detail}` },
      ];
    }

    // Show partial matches (e.g., "C" shows "CD", "CL")
    return baseSuggestions.filter((option) => option.label.startsWith(value));
  }, [value]);

  const SuggestionsContent = (
    <div className={classNames(styles.dateControlSuggestions, className)}>
      <p className={styles.label}>Date Controls:</p>
      {suggestionsList?.map((item, idx) => (
        <Suggestion
          key={idx}
          label={item?.label}
          detail={item?.detail}
          onClick={() => {
            if (item?.label === value) setOpen(false);
            onSuggestionClick(item?.label);
          }}
        />
      ))}
    </div>
  );

  return (
    <>
      {isPopover ? (
        <Popover
          content={suggestionsList?.length > 0 ? SuggestionsContent : null}
          open={open}
          onOpenChange={setOpen}
          placement="bottomLeft"
          trigger="focus"
        >
          <div ref={dateControlRef} className={styles.dateControlSuggestionsContainer}>
            {inputField}
          </div>
        </Popover>
      ) : (
        <div ref={dateControlRef} className={styles.dateControlSuggestionsContainer}>
          {inputField}
          {open && suggestionsList?.length > 0 && SuggestionsContent}
        </div>
      )}
    </>
  );
};
